import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import EpisodeCard from "@/components/feature/EpisodeCard";
import RelatedTitles from "@/components/feature/RelatedTitles";
import SectionHeading from "@/components/base/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import {
  allEpisodes,
  getAdjacentEpisodes,
  getEpisodeById,
  getRecommendations,
} from "@/lib/catalog";
import PlayerPanel from "./components/PlayerPanel";

export default function DetailPage() {
  const { episodeId } = useParams<{ episodeId: string }>();
  const episode = getEpisodeById(episodeId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [noticeOpen, setNoticeOpen] = useState(false);

  const title = episode
    ? `朋友的妈妈4第${episode.number}集在线观看与剧情介绍 - 朋友的妈妈4影视站`
    : "该剧集不存在或已下线 - 朋友的妈妈4影视站";
  const description = episode
    ? `观看朋友的妈妈4第${episode.number}集并了解详细剧情讲解，获取最新影视资讯和相关推荐。`
    : "该剧集不存在或已下线，请返回剧集列表浏览其他章节。";

  usePageMeta(
    title,
    description,
    episode
      ? `朋友的妈妈4第${episode.number}集,${episode.tags.join(",")},朋友的妈妈4剧情`
      : "朋友的妈妈4,朋友的妈妈4剧集列表",
  );

  const adjacency = useMemo(
    () => (episodeId ? getAdjacentEpisodes(episodeId) : { prev: undefined, next: undefined }),
    [episodeId],
  );
  const recommendations = useMemo(
    () => (episode ? getRecommendations(episode, 4) : allEpisodes.slice(0, 4)),
    [episode],
  );

  if (!episode) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <SiteHeader />
        <main className="flex-1 max-w-[1280px] w-full mx-auto px-4 md:px-8 pt-32 md:pt-40 pb-24">
          <div className="mx-auto max-w-xl rounded-lg bg-background-100 border border-background-200/70 px-6 py-14 text-center anim-fade-up">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary-500/15 text-primary-400 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl" />
            </div>
            <h1 className="mt-6 font-heading text-2xl text-foreground-950">
              该剧集不存在或已下线
            </h1>
            <p className="mt-3 text-sm leading-7 text-foreground-500">
              链接可能已失效，或该章节尚未收录。你可以回到剧集列表，从第 1 集重新开始浏览。
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/episodes"
                className="h-10 px-5 inline-flex items-center rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                回到剧集列表
              </Link>
              <Link
                to="/"
                className="h-10 px-5 inline-flex items-center rounded-md border border-background-300/60 text-foreground-800 text-sm hover:border-primary-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const favorited = isFavorite(episode.id);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <section className="relative pt-24 md:pt-28">
        <div className="absolute inset-x-0 top-0 h-[420px] md:h-[520px] overflow-hidden">
          <img
            src={episode.cover}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-top scale-110 blur-xl opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/40 via-background-50/80 to-background-50" />
        </div>

        <div className="relative max-w-[1280px] mx-auto px-4 md:px-8">
          <nav
            aria-label="面包屑"
            className="flex items-center gap-2 text-xs text-foreground-600 flex-wrap"
          >
            <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to="/episodes"
              className="hover:text-primary-400 transition-colors cursor-pointer"
            >
              剧集
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-900">
              第 {episode.number} 集
            </span>
          </nav>

          <div className="mt-6 flex flex-col md:flex-row gap-6">
            <div className="w-[150px] sm:w-[180px] md:w-[200px] shrink-0 rounded-lg overflow-hidden border border-background-300/50 bg-background-900 anim-fade-up">
              <div className="relative w-full aspect-[2/3]">
                <img
                  src={episode.cover}
                  alt={`${episode.title} 剧照`}
                  title={`朋友的妈妈4 ${episode.title} 在线观看`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent" />
                <span className="absolute bottom-2 left-2 font-label text-xs tracking-wider text-accent-500">
                  EP {String(episode.number).padStart(2, "0")}
                </span>
              </div>
            </div>

            <div className="min-w-0 flex-1 anim-fade-up">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 h-5 rounded bg-primary-500 text-background-50 text-[10px] flex items-center">
                  {episode.status}
                </span>
                {episode.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 h-5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] flex items-center"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="mt-3 font-heading text-2xl md:text-4xl leading-tight text-foreground-950">
                {episode.title}
              </h1>
              <p className="mt-2 text-sm md:text-base text-foreground-500">
                {episode.tagline}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-accent-500" />
                  首播 {episode.releaseDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-accent-500" />
                  {episode.duration}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-movie-2-line text-accent-500" />
                  全 {allEpisodes.length} 集
                </span>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(episode.id)}
                  aria-pressed={favorited}
                  className={`inline-flex items-center gap-2 h-10 px-5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                      : "border border-background-300/60 text-foreground-800 hover:border-primary-500 hover:text-foreground-950"
                  }`}
                >
                  <i className={favorited ? "ri-heart-fill" : "ri-heart-line"} />
                  {favorited ? "已收藏本集" : "收藏本集"}
                </button>
                <Link
                  to="/episodes"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300/60 text-sm text-foreground-800 hover:border-accent-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-list-check-2" />
                  剧集列表
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-[1280px] mx-auto px-4 md:px-8 mt-10 md:mt-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-10">
            <PlayerPanel
              episode={episode}
              noticeOpen={noticeOpen}
              onOpenNotice={() => setNoticeOpen(true)}
              onCloseNotice={() => setNoticeOpen(false)}
            />

            <section className="rounded-lg bg-background-100 border border-background-200/70 p-4 md:p-6">
              <h2 className="font-heading text-lg md:text-xl tracking-wide text-foreground-950">
                剧集信息
              </h2>
              <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: "集数", value: `第 ${episode.number} 集 / 共 16 集` },
                  { label: "首播日期", value: episode.releaseDate },
                  { label: "单集时长", value: episode.duration },
                  { label: "播放方式", value: "演示播放器" },
                ].map((item) => (
                  <div key={item.label} className="min-w-0">
                    <dt className="text-[11px] text-foreground-600">{item.label}</dt>
                    <dd className="mt-1 text-xs md:text-sm text-foreground-900">
                      {item.value}
                    </dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 pt-5 border-t border-background-200/70">
                <h3 className="text-sm font-medium text-foreground-900 mb-3 flex items-center gap-2">
                  <i className="ri-user-star-line text-accent-500" />
                  演员及角色
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {episode.cast.map((member) => (
                    <div
                      key={member.name}
                      className="flex items-center gap-3 rounded-md bg-background-200/50 p-3"
                    >
                      <div className="w-9 h-9 rounded-full bg-primary-500/15 text-primary-400 flex items-center justify-center shrink-0">
                        <i className="ri-user-line text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-foreground-900 truncate">{member.name}</p>
                        <p className="text-[11px] text-foreground-600 truncate">
                          {member.role}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="rounded-lg bg-background-100 border border-background-200/70 p-4 md:p-6">
              <h2 className="font-heading text-lg md:text-xl tracking-wide text-foreground-950">
                剧情介绍
              </h2>
              <div className="mt-4 space-y-4">
                {episode.story.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-sm md:text-[15px] leading-8 text-foreground-500"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="mt-6 rounded-md bg-accent-50 border border-accent-500/20 p-3 flex items-start gap-2">
                <i className="ri-information-line text-accent-500 mt-0.5" />
                <p className="text-xs leading-6 text-foreground-600">
                  以上剧情为演示文本，用于展示页面结构与排版效果，不代表真实剧集内容。
                </p>
              </div>
            </section>

            <section>
              <div className="rounded-lg bg-background-100 border border-background-200/70 p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                {adjacency.prev ? (
                  <Link
                    to={`/detail/${adjacency.prev.id}`}
                    className="group flex-1 flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-background-200/60 transition-colors cursor-pointer min-w-0"
                  >
                    <i className="ri-arrow-left-s-line text-lg text-foreground-600 group-hover:text-primary-400 shrink-0" />
                    <span className="min-w-0">
                      <span className="block text-[10px] text-foreground-600">上一集</span>
                      <span className="block text-xs text-foreground-900 truncate">
                        {adjacency.prev.title}
                      </span>
                    </span>
                  </Link>
                ) : (
                  <div className="flex-1 flex items-center gap-3 px-3 py-2.5 rounded-md opacity-40 min-w-0">
                    <i className="ri-arrow-left-s-line text-lg text-foreground-600 shrink-0" />
                    <span className="text-xs text-foreground-600">已是第 1 集</span>
                  </div>
                )}

                <Link
                  to="/episodes"
                  className="shrink-0 inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-md bg-background-200/70 text-[11px] text-foreground-800 hover:bg-background-300/60 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-apps-line" />
                  剧集列表
                </Link>

                {adjacency.next ? (
                  <Link
                    to={`/detail/${adjacency.next.id}`}
                    className="group flex-1 flex items-center justify-end gap-3 px-3 py-2.5 rounded-md hover:bg-background-200/60 transition-colors cursor-pointer min-w-0"
                  >
                    <span className="min-w-0 text-right">
                      <span className="block text-[10px] text-foreground-600">下一集</span>
                      <span className="block text-xs text-foreground-900 truncate">
                        {adjacency.next.title}
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line text-lg text-foreground-600 group-hover:text-primary-400 shrink-0" />
                  </Link>
                ) : (
                  <div className="flex-1 flex items-center justify-end gap-3 px-3 py-2.5 rounded-md opacity-40 min-w-0">
                    <span className="text-xs text-foreground-600">已是最后一集</span>
                    <i className="ri-arrow-right-s-line text-lg text-foreground-600 shrink-0" />
                  </div>
                )}
              </div>
            </section>

            <section>
              <SectionHeading
                eyebrow="Recommended"
                title="相关推荐"
                description="根据本集标签与相邻集数为您推荐。"
              />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                {recommendations.map((item, index) => (
                  <EpisodeCard key={item.id} episode={item} index={index} />
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-4 space-y-4">
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading text-sm tracking-wide text-foreground-950">
                  本剧目录
                </h2>
                <span className="text-[10px] text-foreground-600">共 16 集</span>
              </div>
              <div className="max-h-[340px] overflow-y-auto hide-scrollbar space-y-1 pr-1">
                {allEpisodes.map((item) => {
                  const isCurrent = item.id === episode.id;
                  return (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className={`flex items-center gap-2 px-2.5 py-2 rounded-md text-xs transition-colors cursor-pointer ${
                        isCurrent
                          ? "bg-primary-500/15 text-foreground-950 border border-primary-500/40"
                          : "text-foreground-600 hover:bg-background-200/60 hover:text-foreground-900"
                      }`}
                    >
                      <span
                        className={`font-label tracking-wider text-[10px] ${
                          isCurrent ? "text-primary-400" : "text-foreground-600"
                        }`}
                      >
                        {String(item.number).padStart(2, "0")}
                      </span>
                      <span className="truncate flex-1">{item.title}</span>
                      {item.status === "最新更新" && (
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500 shrink-0" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200/70 p-4">
              <h2 className="font-heading text-sm tracking-wide text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-heart-3-line text-primary-400" />
                收藏状态
              </h2>
              <p className="text-xs leading-6 text-foreground-500">
                {favorited
                  ? "本集已加入收藏，可在「全部剧集 → 我的收藏」中查看。"
                  : "还没有收藏本集。收藏后可在剧集页的收藏分类中快速找到。"}
              </p>
              <button
                type="button"
                onClick={() => toggleFavorite(episode.id)}
                className={`mt-3 w-full h-9 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                  favorited
                    ? "bg-background-200/70 text-foreground-800 hover:bg-background-300/60"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                {favorited ? "取消收藏" : "加入收藏"}
              </button>
              <p className="mt-2 text-[11px] leading-5 text-foreground-600">
                收藏保存在当前浏览器本地，不会上传服务器。
              </p>
            </div>

            <RelatedTitles />
          </aside>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}